import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../shell/store/types";
import { Dialog, Box, Button, Stack, DialogActions, DialogContent, DialogTitle, Typography, IconButton, Alert, AlertTitle, CircularProgress } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { theme } from "@zesty-io/material";
import { UploadThumbnail } from "./UploadThumbnail";
import { Upload, dismissFileUploads } from "../../../../../shell/store/media-revamp";
import { DnDProvider } from "./DnDProvider";
import { UploadButton } from "./UploadButton";
import CloseIcon from "@mui/icons-material/Close";
import { mediaManagerApi } from "../../../../../shell/services/mediaManager";
export const UploadModal: FC = () => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const uploads = useSelector((state: AppState) => state.mediaRevamp.uploads.filter(upload => !upload.replacementFile));
  const filesToUpload = useSelector((state: AppState) => state.mediaRevamp.uploads.filter(upload => upload.status !== "failed" && !upload.replacementFile));
  const ids = filesToUpload.length && {
    currentBinId: filesToUpload[0].bin_id,
    currentGroupId: filesToUpload[0].group_id
  };
  const [loading, setLoading] = useState(false);
  const handleDismiss = async () => {
    setLoading(true);
    try {
      await dispatch(dismissFileUploads());
    } finally {
      setLoading(false);
    }
    if (filesToUpload.length) {
      dispatch(mediaManagerApi.util.invalidateTags([{
        type: "GroupData",
        id: ids.currentGroupId
      }, "BinFiles"]));
    }
  };
  return <>
      <Dialog open={Boolean(uploads.length)} fullScreen onClose={handleDismiss} PaperProps={{
      sx: {
        mx: 10,
        my: 2.5,
        maxHeight: "fill-available",
        maxWidth: 3000
      }
    }} data-sentry-element="Dialog" data-sentry-source-file="UploadModal.tsx">
        <DialogTitle component="div" sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: theme => `1px solid ${theme.palette.border}`
      }} data-sentry-element="DialogTitle" data-sentry-source-file="UploadModal.tsx">
          <UploadHeaderText uploads={uploads} data-sentry-element="UploadHeaderText" data-sentry-source-file="UploadModal.tsx" />
          <Stack direction="row" gap={1.5} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="UploadModal.tsx">
            <UploadButton {...ids} text={t("media.uploadModalUploadMore")} variant="outlined" data-sentry-element="UploadButton" data-sentry-source-file="UploadModal.tsx" />
            <IconButton onClick={handleDismiss} size="small" data-sentry-element="IconButton" data-sentry-source-file="UploadModal.tsx">
              <CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="UploadModal.tsx" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }} data-sentry-element="DialogContent" data-sentry-source-file="UploadModal.tsx">
          <DnDProvider {...ids} sx={{
          flexWrap: "wrap"
        }} dropAreaOverrides={{
          width: "100%",
          height: "calc(100% - 134px)",
          ml: -2,
          mt: -2
        }} data-sentry-element="DnDProvider" data-sentry-source-file="UploadModal.tsx">
            <UploadErrors data-sentry-element="UploadErrors" data-sentry-source-file="UploadModal.tsx" />
            <Box display="grid" sx={{
            width: "100%",
            gap: 3,
            gridTemplateRows: "max-content",
            gridTemplateColumns: "repeat(3, 1fr)",
            [theme.breakpoints.up(1780)]: {
              gridTemplateColumns: "repeat(4, 1fr)"
            },
            [theme.breakpoints.up(2280)]: {
              gridTemplateColumns: "repeat(5, 1fr)"
            }
          }} data-sentry-element="Box" data-sentry-source-file="UploadModal.tsx">
              {filesToUpload.map(file => {
              return <Box position="relative" key={file.uploadID}>
                    <UploadThumbnail file={file} />
                  </Box>;
            })}
            </Box>
          </DnDProvider>
        </DialogContent>
        <DialogActions sx={{
        p: 2,
        borderTop: theme => `1px solid ${theme.palette.border}`
      }} data-sentry-element="DialogActions" data-sentry-source-file="UploadModal.tsx">
          <Button color="primary" variant="contained" disabled={loading} onClick={handleDismiss} data-sentry-element="Button" data-sentry-source-file="UploadModal.tsx">
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
const UploadErrors = () => {
  const {
    t
  } = useTranslation();
  const uploads = useSelector((state: AppState) => state.mediaRevamp.uploads);
  const failedUploads = uploads.filter(upload => upload.status === "failed");
  if (failedUploads.length === 0) return null;
  return <Alert severity="error" sx={{
    width: "100%",
    height: "fit-content"
  }} data-sentry-element="Alert" data-sentry-component="UploadErrors" data-sentry-source-file="UploadModal.tsx">
      <AlertTitle data-sentry-element="AlertTitle" data-sentry-source-file="UploadModal.tsx">
        {t("media.uploadModalUploadErrorTitle", {
        count: failedUploads.length
      })}
        {":"}
      </AlertTitle>
      <>
        {failedUploads.map((file, idx) => {
        return <Box>
              {idx + 1}. {file.filename}
            </Box>;
      })}
        <Box sx={{
        mt: 2
      }} data-sentry-element="Box" data-sentry-source-file="UploadModal.tsx">{t("media.uploadModalUploadErrorHint")}</Box>
      </>
    </Alert>;
};
type UploadHeaderTextProps = {
  uploads: Upload[];
  // i18n discriminator selecting the pluralized noun bundle (not display text).
  headerKeyword?: "file" | "replacedFile";
  showCount?: boolean;
};
export const UploadHeaderText = ({
  uploads,
  headerKeyword = "file",
  showCount = true
}: UploadHeaderTextProps) => {
  const {
    t
  } = useTranslation();
  const filesUploading = uploads?.filter(upload => upload.status === "inProgress");
  const filesUploaded = uploads?.filter(upload => upload.status === "success");
  const filesProcessed = uploads?.filter(upload => upload.status === "success" || upload.status === "failed");
  return <Stack direction="row" alignItems="center" gap={1} alignSelf="flex-start" data-sentry-element="Stack" data-sentry-component="UploadHeaderText" data-sentry-source-file="UploadModal.tsx">
      {filesUploading?.length > 0 ? <Box position="relative" height={32}>
          <CircularProgress variant="determinate" sx={{
        color: theme => theme.palette.grey[theme.palette.mode === "light" ? 200 : 800]
      }} size={32} value={100} />
          <CircularProgress variant="determinate" value={filesProcessed?.length / uploads?.length * 100} size={32} sx={{
        position: "absolute",
        left: 0
      }} />
        </Box> : <CheckCircleRoundedIcon color="success" sx={{
      p: 0.5
    }} />}
      <Typography variant="h5" color="text.primary" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="UploadModal.tsx">
        {(() => {
        const isUploading = filesUploading?.length > 0;
        const count = isUploading ? filesUploading.length : filesUploaded.length;
        const item = t(`media.uploadModalNoun${headerKeyword === "file" ? "File" : "ReplacedFile"}`, {
          count
        });
        if (isUploading) {
          return showCount ? t("media.uploadModalUploadingWithCount", {
            count,
            item
          }) : t("media.uploadModalUploadingNoCount", {
            item
          });
        }
        return showCount ? t("media.uploadModalUploadedWithCount", {
          count,
          item
        }) : t("media.uploadModalUploadedNoCount", {
          item
        });
      })()}
      </Typography>
    </Stack>;
};